// Document-draft storage (task 326.18, finding WEB-004).
//
// DynamicForm auto-saves the form's contents every 30 s and on unmount, so KYC and payment drafts —
// names, dates of birth, addresses, IBANs — sit unencrypted in localStorage. Three problems, all
// fixed here:
//
//   * the key was `draft:${typeCode}:${documentId}` with no operator or tenant in it, so on a shared
//     ops workstation the next operator was offered the previous one's customer data, and switching
//     tenant carried a draft across the isolation boundary;
//   * nothing expired, so a draft typed once stayed forever;
//   * logout removed four auth keys and left every draft behind — and the unmount save fires ON the
//     session-timeout redirect, i.e. the data was written precisely as the operator was logged out.
//
// No React here on purpose: the auth store purges drafts, and the store must not pull in the form
// layer to do it.

const PREFIX = 'draft:';
const TTL_MS = 24 * 60 * 60 * 1000;

export interface DraftEntry {
  timestamp: string;
  data: Record<string, unknown>;
}

/**
 * Key for one draft, namespaced by the operator and tenant that produced it.
 * `scope` is `${userId}:${tenantId}`; an empty scope means "no session", which never saves.
 */
export function buildDraftKey(scope: string, typeCode: string, documentId: string | 'new'): string {
  if (!scope || !typeCode) return '';
  return `${PREFIX}${scope}:${typeCode}:${documentId}`;
}

export function readDraft(key: string): DraftEntry | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const entry = JSON.parse(raw) as DraftEntry;
    if (isExpired(entry)) {
      localStorage.removeItem(key);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function writeDraft(key: string, data: Record<string, unknown>): void {
  if (!key) return;
  try {
    const entry: DraftEntry = { timestamp: new Date().toISOString(), data };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // ignore storage errors (private browsing, quota exceeded)
  }
}

export function clearDraft(key: string): void {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function hasDraft(key: string): boolean {
  return readDraft(key) !== null;
}

/** Removes every draft in this browser. Called on logout and on tenant switch. */
export function purgeAllDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function isExpired(entry: DraftEntry): boolean {
  const saved = Date.parse(entry.timestamp);
  return Number.isNaN(saved) || Date.now() - saved > TTL_MS;
}
